/**
 * Shared chart utilities for multi-series and single-series Recharts rendering.
 * Used by both ChartBlock (chat) and DarkChart (dashboard CompassReportWidget).
 *
 * Recharts requires a "flat data" format for multi-series charts:
 *   [{ day: "Mon", "Denne uken": 100, "Forrige uke": 80 }, ...]
 * Each series maps to a separate dataKey pointing to its column.
 */

export interface ChartSeriesInput {
    name: string;
    data: Record<string, any>[];
    color?: string;
    dataKey?: string;
}

export interface ChartAxisInput {
    label?: string;
    dataKey: string;
    format?: string;
}

export interface ChartKeysResult {
    xKey: string;
    yKeys: string[];
    flatData: Record<string, any>[];
    isMultiSeries: boolean;
}

/**
 * Detect if a series array represents a multi-series chart.
 * Multi-series when: series.length > 1 OR any series has a dataKey property.
 */
export function isMultiSeries(series: ChartSeriesInput[]): boolean {
    if (!series || series.length === 0) return false;
    if (series.length > 1) return true;
    return series.some(s => !!s.dataKey);
}

/**
 * Merge separate series data arrays into a single flat array keyed by xKey.
 * Each series' value is stored under its name (or dataKey).
 *
 * Input:
 *   series[0] = { name: "Denne uken", data: [{Dag: "Man", Antall: 100}, ...] }
 *   series[1] = { name: "Forrige uke", data: [{Dag: "Man", Antall: 80}, ...] }
 *   xKey = "Dag"
 *
 * Output:
 *   [{ Dag: "Man", "Denne uken": 100, "Forrige uke": 80 }, ...]
 */
export function mergeToFlatData(
    series: ChartSeriesInput[],
    xKey: string
): Record<string, any>[] {
    const mergedMap = new Map<string, Record<string, any>>();

    for (const s of series) {
        const seriesKey = s.dataKey || s.name;
        for (const point of s.data) {
            const xValue = String(point[xKey] ?? '');
            const existing = mergedMap.get(xValue) || { [xKey]: point[xKey] };

            if (s.dataKey && point[s.dataKey] !== undefined) {
                // Series has explicit dataKey — use that column value directly
                existing[seriesKey] = point[s.dataKey];
            } else {
                // Find the first numeric value that isn't the xKey
                const numericKeys = Object.keys(point).filter(
                    k => k !== xKey && typeof point[k] === 'number'
                );
                if (numericKeys.length > 0) {
                    existing[seriesKey] = point[numericKeys[0]];
                }
            }

            // Also preserve all original columns for flat data from SQL
            for (const [k, v] of Object.entries(point)) {
                if (!(k in existing)) {
                    existing[k] = v;
                }
            }

            mergedMap.set(xValue, existing);
        }
    }

    return Array.from(mergedMap.values());
}

/**
 * Auto-detect xKey and yKey(s) from data and series configuration.
 * Returns flat data ready for Recharts rendering.
 *
 * Handles three scenarios:
 * 1. Multi-series with dataKey: Each series has its own column → multiple yKeys
 * 2. Multi-series without dataKey: Merge series data into flat format using series names
 * 3. Single-series: Detect one xKey (string column) and one yKey (numeric column)
 */
export function detectChartKeys(
    firstSeriesData: Record<string, any>[],
    series: ChartSeriesInput[],
    xAxis?: ChartAxisInput | null,
    yAxis?: ChartAxisInput | null
): ChartKeysResult {
    const multi = isMultiSeries(series);

    if (multi && series.length > 1) {
        // Determine xKey from config or auto-detect
        const xKey = xAxis?.dataKey || detectXKey(series[0]?.data?.[0]);

        // Check if series have dataKey (Claude's multi-series format)
        const hasDataKeys = series.some(s => !!s.dataKey);

        if (hasDataKeys) {
            // All series share the same flat data (populated from SQL results)
            // DataKey on each series points to its column in the flat data
            const flatData = series[0]?.data || [];
            const yKeys = series.map(s => s.dataKey || s.name);
            return { xKey, yKeys, flatData, isMultiSeries: true };
        }

        // Series have separate data arrays — merge them
        const flatData = mergeToFlatData(series, xKey);
        const yKeys = series.map(s => s.name);
        return { xKey, yKeys, flatData, isMultiSeries: true };
    }

    // Single-series fallback — but check if data itself has multiple numeric columns
    const data = firstSeriesData;
    if (!data || data.length === 0) {
        return {
            xKey: xAxis?.dataKey || 'name',
            yKeys: [yAxis?.dataKey || 'value'],
            flatData: [],
            isMultiSeries: false,
        };
    }

    const xKey = xAxis?.dataKey || detectXKey(data[0]);

    // Auto-detect multi-series from flat data: if data has multiple numeric columns,
    // treat each as a separate series (handles backend auto-detection + edge cases)
    const allKeys = Object.keys(data[0] || {});
    const numericKeys = allKeys.filter(k => k !== xKey && typeof data[0][k] === 'number');

    if (numericKeys.length > 1) {
        return { xKey, yKeys: numericKeys, flatData: data, isMultiSeries: true };
    }

    const yKey = yAxis?.dataKey || detectYKey(data[0], xKey);
    return { xKey, yKeys: [yKey], flatData: data, isMultiSeries: false };
}

/** Detect the first string-like key from a data point (used as X axis). */
function detectXKey(row: Record<string, any> | undefined): string {
    if (!row) return 'name';
    const keys = Object.keys(row);

    for (const key of keys) {
        const val = row[key];
        if (
            typeof val === 'string' ||
            key.toLowerCase().includes('name') ||
            key.toLowerCase().includes('item') ||
            key.toLowerCase().includes('dag') ||
            key.toLowerCase().includes('day') ||
            key.toLowerCase().includes('date') ||
            key.toLowerCase().includes('måned') ||
            key.toLowerCase().includes('uke')
        ) {
            return key;
        }
    }

    return keys[0] || 'name';
}

/** Detect the first numeric key from a data point (used as Y axis). */
function detectYKey(
    row: Record<string, any> | undefined,
    xKey: string
): string {
    if (!row) return 'value';
    const keys = Object.keys(row);

    for (const key of keys) {
        if (key === xKey) continue;
        if (typeof row[key] === 'number') return key;
    }

    return keys.find(k => k !== xKey) || keys[1] || 'value';
}

// ============================================================================
// Conversion helpers for VizSwitcher (table ↔ chart)
// ============================================================================

interface TableBlockDataLike {
    title?: string;
    description?: string;
    columns: { name: string; displayName: string; dataType: string; format?: string; sortable: boolean; filterable: boolean }[];
    rows: Record<string, any>[];
    totalRowCount: number;
    exportable: boolean;
    exportFormats: string[];
}

interface ChartOutputDataLike {
    chartType: string;
    title: string;
    series: ChartSeriesInput[];
    xAxis?: ChartAxisInput | null;
    yAxis?: ChartAxisInput | null;
}

/**
 * Convert TableBlockData to ChartOutputData for rendering a table as a chart.
 * Detects the best x/y keys from the table columns and rows.
 */
export function tableDataToChartData(
    tableData: TableBlockDataLike,
    chartType: string
): ChartOutputDataLike {
    if (!tableData.rows.length || !tableData.columns.length) {
        return {
            chartType,
            title: tableData.title || '',
            series: [{ name: 'Data', data: [], dataKey: 'value' }],
        };
    }

    const firstRow = tableData.rows[0];
    const xKey = detectXKey(firstRow);

    const numericColumns = tableData.columns.filter(
        col => col.name !== xKey && (col.dataType === 'number' || typeof firstRow[col.name] === 'number')
    );

    if (numericColumns.length === 0) {
        // Fallback: try to find any column that has numeric values
        const fallbackNumCol = tableData.columns.find(col => col.name !== xKey);
        const yKey = fallbackNumCol?.name || 'value';
        return {
            chartType,
            title: tableData.title || '',
            series: [{ name: fallbackNumCol?.displayName || yKey, data: tableData.rows, dataKey: yKey }],
            xAxis: { dataKey: xKey },
            yAxis: { dataKey: yKey },
        };
    }

    if (numericColumns.length === 1) {
        const yKey = numericColumns[0].name;
        return {
            chartType,
            title: tableData.title || '',
            series: [{ name: numericColumns[0].displayName, data: tableData.rows, dataKey: yKey }],
            xAxis: { dataKey: xKey },
            yAxis: { dataKey: yKey },
        };
    }

    // Multi-series: each numeric column becomes a series
    const series: ChartSeriesInput[] = numericColumns.map(col => ({
        name: col.displayName,
        data: tableData.rows,
        dataKey: col.name,
    }));

    return {
        chartType,
        title: tableData.title || '',
        series,
        xAxis: { dataKey: xKey },
    };
}

/**
 * Convert ChartOutputData to TableBlockData for rendering a chart as a table.
 * Flattens the chart data into columns and rows.
 */
export function chartDataToTableData(
    chartData: ChartOutputDataLike
): TableBlockDataLike {
    const { xKey, yKeys, flatData } = detectChartKeys(
        chartData.series?.[0]?.data || [],
        chartData.series,
        chartData.xAxis,
        chartData.yAxis
    );

    if (!flatData.length) {
        return {
            title: chartData.title,
            columns: [],
            rows: [],
            totalRowCount: 0,
            exportable: true,
            exportFormats: ['csv', 'xlsx', 'json', 'html'],
        };
    }

    // Build column definitions from keys
    const allColumnKeys = [xKey, ...yKeys];
    const firstRow = flatData[0];

    const columns = allColumnKeys.map(key => {
        const val = firstRow[key];
        const isNum = typeof val === 'number';
        // Try to get display name from series
        const matchedSeries = chartData.series.find(s => (s.dataKey || s.name) === key);
        const displayName = matchedSeries?.name || key;

        return {
            name: key,
            displayName: key === xKey ? key : displayName,
            dataType: isNum ? 'number' : 'string',
            sortable: true,
            filterable: false,
        };
    });

    return {
        title: chartData.title,
        columns,
        rows: flatData,
        totalRowCount: flatData.length,
        exportable: true,
        exportFormats: ['csv', 'xlsx', 'json', 'html'],
    };
}

// ============================================================================
// Data-shape validator (suggestVizType)
// ============================================================================

export interface VizSuggestion {
    suggestedType: 'kpi' | 'bar' | 'line' | 'area' | 'pie' | 'table';
    reason: string;
    confidence: 'high' | 'medium' | 'low';
}

/** Date/time column name patterns */
const DATE_PATTERNS = /^(dato|date|dag|day|uke|week|m[aå]ned|month|year|[aå]r|tid|time|period|kvartal|quarter)/i;

/**
 * Analyze data shape and suggest the best visualization type.
 * Used as a "layer 2" validator after Claude proposes a viz type.
 */
export function suggestVizType(
    rows: Record<string, any>[],
    columns?: { name: string; dataType: string }[],
    currentType?: string
): VizSuggestion {
    if (!rows || rows.length === 0) {
        return { suggestedType: 'table', reason: 'Ingen data', confidence: 'low' };
    }

    const keys = Object.keys(rows[0]);
    const numericKeys = keys.filter(k => typeof rows[0][k] === 'number');
    const stringKeys = keys.filter(k => typeof rows[0][k] === 'string');
    const dateKeys = keys.filter(k => DATE_PATTERNS.test(k));

    // Rule 1: Single row with only numbers → KPI
    if (rows.length === 1 && numericKeys.length >= 1 && numericKeys.length === keys.length) {
        return { suggestedType: 'kpi', reason: 'Enkelt tall — best som KPI', confidence: 'high' };
    }

    // Rule 1b: Single row with few values (1-4 numeric) → KPI
    if (rows.length === 1 && numericKeys.length >= 1 && numericKeys.length <= 4) {
        return { suggestedType: 'kpi', reason: '1-4 nøkkeltall — best som KPI', confidence: 'high' };
    }

    // Rule 2: Has date/time column + numeric → line chart
    if (dateKeys.length > 0 && numericKeys.length >= 1) {
        return { suggestedType: 'line', reason: 'Tidsserie-data — best som linjediagram', confidence: 'medium' };
    }

    // Rule 3: >50 rows → table
    if (rows.length > 50) {
        return { suggestedType: 'table', reason: 'Mange rader (>50) — best som tabell', confidence: 'medium' };
    }

    // Rule 4: Categorical string + numeric, ≤ 20 rows → bar
    if (stringKeys.length >= 1 && numericKeys.length >= 1 && rows.length <= 20) {
        return { suggestedType: 'bar', reason: 'Kategori + tall — best som stolpediagram', confidence: 'medium' };
    }

    // Rule 5: Categorical string + numeric, 20-50 rows → still bar but lower confidence
    if (stringKeys.length >= 1 && numericKeys.length >= 1 && rows.length <= 50) {
        return { suggestedType: 'bar', reason: 'Kategori + tall — stolpediagram mulig', confidence: 'low' };
    }

    // Default: keep current or suggest table
    const fallback = (currentType as VizSuggestion['suggestedType']) || 'table';
    return { suggestedType: fallback, reason: 'Standard visning', confidence: 'low' };
}
